import { useEffect, useState } from "react";
import { Search, BookOpen } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { useSite } from "../context/SiteContext";

export default function Library() {
  const { settings } = useSite();
  const p = settings.pages.library || { label: "Resources", title: "Library", subtitle: "Browse the college library catalogue. Books are issued at the library counter." };
  const [books, setBooks] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  useEffect(() => { api.listBooks().then((r) => setBooks(r.data)).catch(() => {}); }, []);

  const categories = ["All", ...Array.from(new Set(books.map((b) => b.category).filter(Boolean)))];
  const filtered = books.filter((b) => {
    const matchesQ = `${b.title} ${b.author} ${b.isbn}`.toLowerCase().includes(q.toLowerCase());
    const matchesCat = cat === "All" || b.category === cat;
    return matchesQ && matchesCat;
  });

  return (
    <div data-testid="library-page">
      <PageHero label={p.label} title={p.title} subtitle={p.subtitle} />
      {settings.images && settings.images.library && (
        <div className="w-full h-56 md:h-80 overflow-hidden relative">
          <img data-testid="library-banner" src={settings.images.library} alt="College Library" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-forest/40 to-transparent" />
        </div>
      )}
      <section className="bg-bone py-16 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          {/* search + filter */}
          <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-10">
            <div className="relative w-full md:max-w-md">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest/40" />
              <input
                data-testid="library-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by title, author or ISBN…"
                className="w-full bg-white border border-line focus:border-gold outline-none pl-11 pr-4 py-3 rounded-full text-sm focus:ring-2 focus:ring-gold/30"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button key={c} data-testid={`library-cat-${c}`} onClick={() => setCat(c)} className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full border transition-colors ${cat === c ? "bg-forest text-bone border-forest" : "border-line text-forest/60 hover:border-forest"}`}>{c}</button>
              ))}
            </div>
          </div>

          <div className="border border-line bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bone text-forest/60 uppercase text-xs tracking-widest">
                <tr>
                  <th className="text-left px-5 py-3">Title</th>
                  <th className="text-left px-5 py-3">Author</th>
                  <th className="text-left px-5 py-3">Category</th>
                  <th className="text-left px-5 py-3">ISBN</th>
                  <th className="text-center px-5 py-3">Availability</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map((b) => (
                  <tr key={b.id} data-testid="library-book-row" className="hover:bg-bone/60">
                    <td className="px-5 py-4 font-serif text-lg text-forest">{b.title}</td>
                    <td className="px-5 py-4 text-muted">{b.author}</td>
                    <td className="px-5 py-4"><span className="text-xs uppercase tracking-widest text-gold">{b.category}</span></td>
                    <td className="px-5 py-4 font-mono text-xs text-muted">{b.isbn}</td>
                    <td className="px-5 py-4 text-center">
                      {b.available_copies > 0 ? (
                        <span className="text-xs uppercase tracking-widest text-forest bg-forest/10 px-3 py-1 rounded-full">{b.available_copies} / {b.total_copies} available</span>
                      ) : (
                        <span className="text-xs uppercase tracking-widest text-destructive bg-destructive/10 px-3 py-1 rounded-full">Issued out</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="p-12 text-center text-muted">
                <BookOpen size={28} className="mx-auto mb-3 opacity-40" />
                No books found.
              </div>
            )}
          </div>
          <Reveal><p className="text-xs text-muted mt-6">Showing {filtered.length} of {books.length} titles. To borrow a book, please visit the library counter.</p></Reveal>
        </div>
      </section>
    </div>
  );
}
